/**
 * AI 가드레일 관문 — 서버가 AI 호출을 멈춘 두 경우를 화면이 받아 처리하고 **같은 요청을 다시 보낸다**.
 *   GUARDRAIL_ACK_REQUIRED (428)      — 처음 사용 전 확인을 안 했다 → 가드레일 창(확인 모드)
 *   GUARDRAIL_DECISION_REQUIRED (409) — 「물어보고」 규칙에 걸린 값 → 결정 창 → 저장 → 다시
 * docs/features/21-ai-guardrail.md
 *
 * 창을 띄우는 것은 `GuardrailHost` 가 등록한 처리기다. 요청을 보내는 길이 둘(axios · fetch)이라
 * 둘 다 여기 `withGuardrail` / `installGuardrailInterceptor` 를 지난다. 동시에 여러 요청이 걸려도 창은 하나다.
 */
// GUARDRAIL_REVIEW_REQUIRED (409, 2026-10-20) — 보내기 전 검사: 보낼 글에 규칙에 걸린 값이 있다 → 목록 확인 창(변조·빼기·그대로) → 그 요청 한 번만 → 다시
// (ACK 는 서버가 더는 내지 않지만 옛 서버를 위해 남겨 둔다)
export const GUARD_ERRORS = ['GUARDRAIL_ACK_REQUIRED', 'GUARDRAIL_DECISION_REQUIRED', 'GUARDRAIL_REVIEW_REQUIRED'];

let handler = null;
let inflight = null;

/** @param {(errorType: string) => Promise<boolean>} fn — true 면 다시 보낸다 */
export function setGuardrailHandler(fn) {
  handler = fn;
  return () => { if (handler === fn) handler = null; };
}

/**
 * 지금 가드레일 창을 기다리는 요청이 있는가 — 진행 표시가 「AI 가 쓰는 중」 대신 「가드레일 확인을 기다림」 이라고 말하게
 * (확인 창을 못 보고 멈춘 줄 알았던 신고 2026-10-08). 값: null | errorType
 */
let waiting = null;
const waitListeners = new Set();
const setWaiting = (v) => { waiting = v; waitListeners.forEach((fn) => { try { fn(v); } catch (_e) { /* 표시용 */ } }); };
export function onGuardrailWaiting(fn) {
  waitListeners.add(fn);
  fn(waiting);
  return () => waitListeners.delete(fn);
}

/** 처리기에 넘긴다. 처리기가 없으면(아직 안 붙음) 다시 보내지 않는다 — 오류 문구가 그대로 뜬다 */
export function resolveGuardrail(errorType) {
  if (!handler || !GUARD_ERRORS.includes(errorType)) return Promise.resolve(false);
  if (!inflight) {
    setWaiting(errorType);
    inflight = Promise.resolve()
      .then(() => handler(errorType))
      .catch(() => false)
      .finally(() => { inflight = null; setWaiting(null); });
  }
  return inflight;
}

const typeOf = (err) => (err && (err.errorType
  || (err.response && err.response.data && err.response.data.errorType)
  || (err.body && err.body.errorType))) || '';

/**
 * fetch 쪽 — 요청 함수를 감싼다. 가드레일로 멈췄으면 처리 후 **한 번만** 다시 부른다
 * (결정 뒤에도 또 걸리면 그 사이 정책이 바뀐 것이라 사람에게 다시 맡긴다).
 */
export async function withGuardrail(fn) {
  try {
    return await fn();
  } catch (err) {
    const t = typeOf(err);
    if (GUARD_ERRORS.includes(t) && await resolveGuardrail(t)) return fn();
    throw err;
  }
}

/** axios 쪽 — 응답 가로채기. 다시 보낸 요청에는 표식을 달아 두 번 돌지 않는다 */
export function installGuardrailInterceptor(instance) {
  const id = instance.interceptors.response.use(undefined, async (error) => {
    const t = typeOf(error);
    const cfg = error && error.config;
    if (cfg && !cfg.__guardrailRetried && GUARD_ERRORS.includes(t) && await resolveGuardrail(t)) {
      return instance.request({ ...cfg, __guardrailRetried: true });
    }
    return Promise.reject(error);
  });
  return () => instance.interceptors.response.eject(id);
}

// ── 내보내기 검사(가드레일 4) ─────────────────────────────────────────────
// 암호화되지 않은 파일(리포트·로그)로 나가는 글에 금지 항목이 있으면 **사람이** 빼기·치환·그대로 중에 고른다.
// 걸린 것이 없으면 묻지 않는다. 검사에 실패하면(서버 오류) 내보내기를 막지 않고 원문으로 간다 — 그 사실을 알린다.
let exportHandler = null;

/** @param {(scan) => Promise<'asis'|'mask'|'exclude'|null>} fn */
export function setExportHandler(fn) {
  exportHandler = fn;
  return () => { if (exportHandler === fn) exportHandler = null; };
}

/**
 * @param {string} text
 * @param {{ scan: Function, redact: Function }} api  — services/guardrail 의 scanText·redactText
 * @returns {Promise<{ text: string, mode: string } | null>} null = 취소
 */
export async function guardExport(text, api) {
  let scan;
  try { scan = await api.scan(text); } catch (_e) { return { text, mode: 'unchecked' }; }
  if (!scan || !scan.total || !exportHandler) return { text, mode: 'clean' };
  const mode = await exportHandler(scan);
  if (!mode) return null;
  if (mode === 'asis') return { text, mode };
  const r = await api.redact(text, mode);
  return { text: r.text, mode };
}
